// 캐릭터+무기가 가장 많이 쓰는 루트의 마지막 도착 지역을 구하고, 24명 전체를 지역별로 집계한다.
// 루트 통계(character-detail의 routeStats)에서 게임 수가 가장 많은 루트를 고르고,
// 그 루트 상세(route-detail)의 동선(paths, 순서 있음) 마지막 지역을 도착 지역으로 본다.

export const UNKNOWN_AREA = '알 수 없음'; // 동선이 없거나 API가 도착 지역을 알려주지 않은 경우
export const NO_ROUTE_AREA = '루트 통계 없음'; // 그 캐릭터+무기의 루트 통계가 서버에 없는 경우
export const FAILED_AREA = '조회 실패'; // 네트워크 오류 등으로 조회하지 못한 경우

export function mostUsedRouteId(routeStats) {
  if (!routeStats || !routeStats.length) {
    return null;
  }
  return routeStats.reduce((best, s) => (s.gameCount > best.gameCount ? s : best)).routeIdOfStart;
}

export function lastArea(paths) {
  if (!paths || !paths.length) {
    return UNKNOWN_AREA;
  }
  return paths[paths.length - 1] || UNKNOWN_AREA;
}

// 지도에 올리지 않는 항목은 맨 뒤에 이 순서로 둔다
const SPECIAL_ORDER = [UNKNOWN_AREA, NO_ROUTE_AREA, FAILED_AREA];
const isSpecial = (area) => SPECIAL_ORDER.includes(area);

/**
 * entries: [{ teamIndex, characterName, weaponName, area }] — area가 없으면(null/undefined) 아직 조회 중이다.
 * 도착 지역별 { area, count, myCount, members }를 많은 순으로 돌려준다(같으면 이름 순, 알 수 없음/조회 실패는 맨 뒤).
 */
export function tallyDestinations(entries, myTeamIndex) {
  const byArea = new Map();
  let pending = 0;
  entries.forEach((entry) => {
    if (!entry.area) {
      pending += 1;
      return;
    }
    if (!byArea.has(entry.area)) {
      byArea.set(entry.area, { area: entry.area, count: 0, myCount: 0, members: [] });
    }
    const group = byArea.get(entry.area);
    group.count += 1;
    if (entry.teamIndex === myTeamIndex) {
      group.myCount += 1;
    }
    group.members.push({ teamIndex: entry.teamIndex, characterName: entry.characterName, weaponName: entry.weaponName });
  });
  const groups = [...byArea.values()].sort((a, b) => {
    if (isSpecial(a.area) !== isSpecial(b.area)) {
      return isSpecial(a.area) ? 1 : -1;
    }
    if (isSpecial(a.area)) {
      return SPECIAL_ORDER.indexOf(a.area) - SPECIAL_ORDER.indexOf(b.area);
    }
    return b.count - a.count || a.area.localeCompare(b.area, 'ko');
  });
  return { groups, total: entries.length, pending };
}

/**
 * daysList(예: [14, 90])의 기간을 차례로 fetchByDays(days)로 조회해, 처음으로 데이터가 있는(빈 배열이 아닌) 결과를 돌려준다.
 * 최근 기간에 통계가 없는 캐릭터+무기(드물게 쓰이거나 데이터가 오래된 서버)는 더 긴 기간으로 대신한다. 모두 비어 있으면 빈 배열이다.
 */
export async function fetchWithFallbackDays(daysList, fetchByDays) {
  for (const days of daysList) {
    // 기간 순서대로 하나씩 확인해야 하므로 반복 안에서 기다린다
    // eslint-disable-next-line no-await-in-loop
    const result = await fetchByDays(days);
    if (result && result.length) {
      return result;
    }
  }
  return [];
}

/**
 * 집계한 지역(groups)을 지도에 올릴 수 있는 것(positions에 위치가 있는 지역)과 아닌 것(알 수 없음, 루트 통계 없음, 조회 실패, 위치를 모르는 지역)으로 나눈다.
 */
export function splitMappableGroups(groups, positions) {
  return {
    onMap: groups.filter((g) => positions[g.area]),
    offMap: groups.filter((g) => !positions[g.area]),
  };
}

/** 루트 상세(resolver가 돌려준 값, 루트가 없으면 null)의 도착 지역 */
export function destinationOf(detail) {
  return detail ? lastArea(detail.paths) : NO_ROUTE_AREA;
}

/**
 * (캐릭터, 무기) → 가장 많이 쓰는 루트 상세 조회기(루트가 없으면 null). 도착 지역과 후반 교체 아이템(오브젝트)이 같은 조회를 쓴다.
 * 같은 조합은 한 번만 조회하고(진행 중인 조회도 공유) 실패는 캐시하지 않는다.
 * fetchRouteStats(캐릭터, 무기) → routeStats, fetchRouteDetail(routeId) → 루트 상세. 동시에 진행하는 조회는 concurrency개까지다.
 */
export function createRouteResolver({ fetchRouteStats, fetchRouteDetail, concurrency = 4 }) {
  const cache = new Map();
  const waiting = [];
  let running = 0;

  const next = () => {
    while (running < concurrency && waiting.length) {
      running += 1;
      waiting.shift()();
    }
  };
  const limited = (task) => new Promise((resolve, reject) => {
    waiting.push(() => {
      task().then(resolve, reject).finally(() => {
        running -= 1;
        next();
      });
    });
    next();
  });

  const lookup = async (characterName, weaponName) => {
    const routeId = mostUsedRouteId(await fetchRouteStats(characterName, weaponName));
    if (routeId === null || routeId === undefined) {
      return null;
    }
    return fetchRouteDetail(routeId);
  };

  return {
    resolve(characterName, weaponName) {
      const key = `${characterName}|${weaponName}`;
      if (!cache.has(key)) {
        const promise = limited(() => lookup(characterName, weaponName));
        cache.set(key, promise);
        promise.catch(() => cache.delete(key));
      }
      return cache.get(key);
    },
  };
}
